"use client"

import Link from "next/link"
import SiteHeader from "@/components/shared/site-header"
import SiteFooter from "@/components/shared/site-footer"
import { StarBorder } from "@/components/ui/star-border"
import { AlertTriangle, ArrowLeft, RefreshCw, Server } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ErrorPageProps {
  statusCode: number
  title: string
  description: string
  showRefresh?: boolean
}

export function ErrorPage({ statusCode, title, description, showRefresh = true }: ErrorPageProps) {
  const getIcon = () => {
    switch (statusCode) {
      case 303:
        return <ArrowLeft className="h-16 w-16 text-primary" />
      case 400:
        return <AlertTriangle className="h-16 w-16 text-amber-500" />
      case 404:
        return <AlertTriangle className="h-16 w-16 text-primary" />
      case 500:
        return <Server className="h-16 w-16 text-red-500" />
      default:
        return <AlertTriangle className="h-16 w-16 text-primary" />
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full py-12 md:py-24 lg:py-32">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-6 text-center">
              <div className="rounded-full bg-muted p-6">{getIcon()}</div>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                  {statusCode} - {title}
                </h1>
                <p className="mx-auto max-w-[700px] text-muted-foreground md:text-xl">{description}</p>
              </div>
              <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
                <Link href="/" passHref>
                  <StarBorder className="inline-flex items-center justify-center">
                    Return to Homepage
                  </StarBorder>
                </Link>
                {showRefresh && (
                  <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh Page
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  )
}

